import moment from 'moment';
import { IndustryModeEnum } from '../api/constants';

export type IndustryMode =
  (typeof IndustryModeEnum)[keyof typeof IndustryModeEnum];

export type RoleName = 'owner' | (string & {});

export interface ShiftFlags {
  isCancelled?: boolean;
  isDisputed?: boolean;
  isCompletionAcknowledged?: boolean;
  isCompleted?: boolean;
}

export interface ShiftRatingSource {
  rating?: number | string | null;
  comment?: string | null;
  date?: string | null;
  [key: string]: unknown;
}

export interface ShiftLike {
  officialEndTime?: string | Date | null;
  pharmacy?: {
    ratingOptionTimer?: number | string | null;
  } | null;
  flags?: ShiftFlags | null;
  shiftRating?: ShiftRatingSource | null;
  workerShiftRating?: ShiftRatingSource | null;
  locationShiftRating?: ShiftRatingSource | null;
}

// Resolve the rating object to use based on industry and role.
const resolveRatingSource = (
  shift: ShiftLike | null | undefined,
  industryMode: IndustryMode | null | undefined,
  roleName: RoleName | null | undefined,
): { value: ShiftRatingSource | null | undefined } => {
  const resolvedRole = roleName ?? null;
  const resolvedIndustry = industryMode ?? null;

  if (
    resolvedIndustry === IndustryModeEnum.Pharmaceutical &&
    resolvedRole === 'owner'
  ) {
    return { value: shift?.shiftRating };
  }
  const ratingSource =
    resolvedRole === 'owner'
      ? shift?.workerShiftRating
      : shift?.locationShiftRating;
  return { value: ratingSource };
};

// True while the rating window (end time + configured hours) is still open.
export const isShiftRatingWindowOpen = (
  shift: ShiftLike | null | undefined,
): boolean => {
  if (!shift) {
    return false;
  }
  if (shift.flags?.isCancelled) {
    return false;
  }
  const endTime = shift?.officialEndTime ?? null;
  if (!endTime) {
    return false;
  }
  const endMoment = moment(endTime);
  if (!endMoment.isValid()) {
    return false;
  }

  const timerFromPharmacyHours = Number(shift?.pharmacy?.ratingOptionTimer);
  if (!Number.isFinite(timerFromPharmacyHours)) {
    return false;
  }

  const cutoff = endMoment.clone().add(timerFromPharmacyHours, 'hours');
  return moment().isSameOrBefore(cutoff);
};


// Decides whether the Rate Shift action should be shown.
export const shouldShowShiftRating = (
  shift: ShiftLike | null | undefined,
  industryMode: IndustryMode | null | undefined,
  roleName: RoleName | null | undefined,
): boolean => {
  if (!shift) {
    return false;
  }
  if (!isShiftRatingWindowOpen(shift)) {
    return false;
  }

  const { flags } = shift;

  if (roleName === 'owner') {
    if (
      !flags ||
      flags.isCancelled ||
      // flags.isRated ||
      // flags.isDisputed ||
       (flags.isDisputed && !flags.isCompletionAcknowledged) ||
      !flags.isCompletionAcknowledged 
    ) {
      return false;
    }
  }
  else {
    if (
      !flags ||
      flags.isCancelled ||
      // flags.isRated ||
      // flags.isDisputed ||
      (flags.isDisputed && !flags.isCompletionAcknowledged) ||
      !(flags.isCompletionAcknowledged || flags.isCompleted)
    ) {
      return false;
    }
  }
  

  
  const { value: ratingSource } = resolveRatingSource(
    shift,
    industryMode,
    roleName,
  );
  if (ratingSource != null) {
    return false;
  }

  return true;
};

// Returns true once a rating object exists for the user/role context.
export const isShiftRated = (
  shift: ShiftLike | null | undefined,
  industryMode: IndustryMode | null | undefined,
  roleName: RoleName | null | undefined,
): boolean => {
  if (!shift) {
    return false;
  }
  const { value: ratingSource } = resolveRatingSource(
    shift,
    industryMode,
    roleName,
  );
  return ratingSource != null;
};

// Allows rating while awaiting hours confirmation when the window is open and not rated.
export const shouldShowShiftRatingWhileAwaitingHours = (
  shift: ShiftLike | null | undefined,
  industryMode: IndustryMode | null | undefined,
  roleName: RoleName | null | undefined,
) => {

  if (!shift) {
    return false;
  }

  const { flags } = shift;
  if (!flags?.isCompleted || flags.isDisputed) {
    return false;
  }
  return (
    isShiftRatingWindowOpen(shift) &&
    !isShiftRated(shift, industryMode, roleName)
  );
};

// Extracts the numeric rating value for the current role context.
export const getShiftRatingValue = (
  shift: ShiftLike | null | undefined,
  industryMode: IndustryMode | null | undefined,
  roleName: RoleName | null | undefined,
): number | null => {
  if (!shift) {
    return null;
  }
  const { value: ratingSource } = resolveRatingSource(
    shift,
    industryMode,
    roleName,
  );
  const rawRating = ratingSource?.rating ?? null;
  const parsedRating = rawRating == null ? null : Number(rawRating);
  if (parsedRating == null || !Number.isFinite(parsedRating)) {
    return null;
  }
  return parsedRating;
};

// Returns the full rating object (rating/comment/date) for the current role context.
export const getShiftRatingSource = (
  shift: ShiftLike | null | undefined,
  industryMode: IndustryMode | null | undefined,
  roleName: RoleName | null | undefined,
): ShiftRatingSource | null => {
  if (!shift) {
    return null;
  }
  const { value: ratingSource } = resolveRatingSource(
    shift,
    industryMode,
    roleName,
  );
  return ratingSource ?? null;
};